import type { FormEvent } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { catalogPageSchema } from "@eaf/contracts";
import { EmptyState, ErrorState, LoadingState, PageFrame } from "@eaf/ui";
import { publicApi } from "../api/client";
import { ArtworkCard } from "../components/artwork-card";
import { Seo } from "../components/seo";

export function GalleryPage() {
  const [params, setParams] = useSearchParams();
  const normalized = new URLSearchParams(params);
  if (!normalized.has("page")) normalized.set("page", "1");
  if (!normalized.has("pageSize")) normalized.set("pageSize", "15");
  if (!normalized.has("sort")) normalized.set("sort", "seq-asc");
  const query = useQuery({
    queryKey: ["catalog", normalized.toString()],
    queryFn: () => publicApi(`/catalog?${normalized}`, catalogPageSchema)
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next = new URLSearchParams();
    next.set("page", "1");
    next.set("pageSize", "15");
    for (const key of ["q", "orientation", "medium", "sort"]) {
      const field = form.get(key);
      const value = typeof field === "string" ? field : "";
      if (value) next.set(key, value);
    }
    setParams(next);
  };
  const page = query.data?.pagination;
  return (
    <PageFrame>
      <Seo
        title="Gallery | Edmund Alan Fain"
        description="Explore the published Edmund Alan Fain collection."
      />
      <header className="page-header">
        <p className="eyebrow">The collection</p>
        <h1>Gallery</h1>
        <p className="lead">Explore a growing digital record of original works.</p>
      </header>
      <Form key={params.toString()} className="catalog-controls" onSubmit={submit}>
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Label htmlFor="gallery-search">Search</Form.Label>
            <Form.Control
              id="gallery-search"
              name="q"
              defaultValue={params.get("q") ?? ""}
              placeholder="Title, registration code, or description"
            />
          </Col>
          <Col sm={6} md={2}>
            <Form.Label htmlFor="gallery-orientation">Orientation</Form.Label>
            <Form.Select
              id="gallery-orientation"
              name="orientation"
              defaultValue={params.get("orientation") ?? ""}
            >
              <option value="">All</option>
              {query.data?.filters.orientations.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </Form.Select>
          </Col>
          <Col sm={6} md={2}>
            <Form.Label htmlFor="gallery-medium">Medium</Form.Label>
            <Form.Select
              id="gallery-medium"
              name="medium"
              defaultValue={params.get("medium") ?? ""}
            >
              <option value="">All</option>
              {query.data?.filters.mediums.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </Form.Select>
          </Col>
          <Col sm={8} md={2}>
            <Form.Label htmlFor="gallery-sort">Sort</Form.Label>
            <Form.Select
              id="gallery-sort"
              name="sort"
              defaultValue={params.get("sort") ?? "seq-asc"}
            >
              <option value="seq-asc">Sequence</option>
              <option value="seq-desc">Sequence, descending</option>
              <option value="title-asc">Title, A–Z</option>
              <option value="title-desc">Title, Z–A</option>
              <option value="edited-desc">Recently updated</option>
            </Form.Select>
          </Col>
          <Col sm={4} md={2}>
            <Button type="submit" className="w-100">
              Apply
            </Button>
          </Col>
        </Row>
      </Form>
      {query.isLoading ? (
        <LoadingState label="Loading gallery" />
      ) : query.isError || !query.data ? (
        <ErrorState retry={() => void query.refetch()}>
          The gallery is temporarily unavailable.
        </ErrorState>
      ) : query.data.items.length === 0 ? (
        <EmptyState title="No artwork matched">
          <p>Try removing one or more filters.</p>
        </EmptyState>
      ) : (
        <Row className="g-4">
          {query.data.items.map((artwork) => (
            <Col key={artwork.id} xs={12} sm={6} lg={4}>
              <ArtworkCard artwork={artwork} />
            </Col>
          ))}
        </Row>
      )}
      {page ? (
        <nav className="pager" aria-label="Gallery pages">
          <Button
            variant="outline-light"
            disabled={page.page <= 1}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("page", String(page.page - 1));
              setParams(next);
            }}
          >
            Previous
          </Button>
          <span>
            Page {page.page} of {page.totalPages} · {page.totalItems} works
          </span>
          <Button
            variant="outline-light"
            disabled={page.page >= page.totalPages}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("page", String(page.page + 1));
              setParams(next);
            }}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </PageFrame>
  );
}
